import { Ref, useCallback, useRef, useState } from 'react'
import clsx from 'clsx'
import { HugeiconsIcon, HugeiconsIconProps } from '@hugeicons/react'
import { useLingui } from '@lingui/react/macro'

import {
  Cancel01Icon,
  ViewIcon,
  ViewOffIcon
} from '@hugeicons/core-solid-rounded'

export type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  Icon?: HugeiconsIconProps['icon'] | React.FC
  invalid?: boolean
  ref?: Ref<HTMLInputElement>
  isResetable?: boolean
}

function renderIcon(Icon: HugeiconsIconProps['icon'] | React.FC) {
  if (typeof Icon === 'function') return <Icon />

  if (Array.isArray(Icon)) {
    return <HugeiconsIcon icon={Icon} className="size-full" />
  }

  return null
}

const Input = function Input({
  Icon,
  className,
  invalid = false,
  ref: forwardedRef,
  value,
  type,
  isResetable = false,
  onChange,
  ...input
}: Props) {
  const { t } = useLingui()

  const internalRef = useRef<HTMLInputElement>(null)
  const ref = (forwardedRef || internalRef) as React.RefObject<HTMLInputElement>

  const [showPassword, setShowPassword] = useState(false)
  const isPasswordType = type === 'password'

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const resetValue = useCallback(() => {
    const element = ref.current
    if (!element || !onChange) return

    element.value = ''

    // Create synthetic event that matches React's ChangeEvent
    const syntheticEvent = {
      target: element,
      currentTarget: element
    } as React.ChangeEvent<HTMLInputElement>

    onChange(syntheticEvent)
  }, [ref, onChange])

  const hasRightIcon = isResetable || isPasswordType

  return (
    <div className="relative">
      {Icon && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 size-5 pointer-events-none z-10">
          {renderIcon(Icon)}
        </div>
      )}

      <input
        className={clsx(
          'w-full rounded-small border border-text bg-white text-regular text-text placeholder:text-text-light outline-hidden py-2',
          'focus:outline-solid focus:outline-primary',
          'disabled:cursor-not-allowed disabled:border-text-light disabled:text-text-light invalid:border-red-500',
          'read-only:border-none read-only:cursor-text read-only:focus:outline-hidden read-only:rounded-none read-only:text-text',
          // Dynamic padding based on icons
          Icon && hasRightIcon && 'px-12',
          Icon && !hasRightIcon && 'pl-12 pr-4',
          !Icon && hasRightIcon && 'pl-4 pr-12',
          !Icon && !hasRightIcon && 'px-4',
          !Icon && 'read-only:p-0',
          invalid && 'border-red-500',
          className
        )}
        {...input}
        onChange={onChange}
        type={isPasswordType ? (showPassword ? 'text' : 'password') : type}
        value={value != null ? value : ''}
        aria-invalid={invalid}
        ref={ref}
      />
      {isResetable && !!value && (
        <button
          type="button"
          onClick={resetValue}
          className="absolute right-4 top-1/2 -translate-y-1/2 size-5 z-10 text-text-light hover:text-text transition-colors rounded-small cursor-pointer"
          tabIndex={0}
          aria-label={t`Clear input`}
        >
          <HugeiconsIcon icon={Cancel01Icon} className="size-full" />
        </button>
      )}
      {isPasswordType && (
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-4 top-1/2 -translate-y-1/2 size-5 z-10 text-text-light hover:text-text transition-colors rounded-small cursor-pointer"
          tabIndex={0}
          aria-label={showPassword ? t`Hide password` : t`Show password`}
        >
          <HugeiconsIcon
            icon={showPassword ? ViewOffIcon : ViewIcon}
            className="size-full"
          />
        </button>
      )}
    </div>
  )
}

export default Input
